import React from 'react'

import WSManager from '../connection/websocket'

import { logger } from '../utils'

import {
  ACCOUNT_AUTHORIZED,
  ACCOUNT_LOGOUT,
  ACCOUNT_RESOLVING,
  ACCOUNT_UPDATE, ACCOUNT_VERIFICATION_UPDATE,
  BETS_COUNT_UPDATE,
  BONUS_ACTIVATE,
  BONUS_DEACTIVATE,
  BONUS_STATUS,
  CONNECTION_UPDATE,
  PREFERENCES_UPDATE,
  SET_BETSLIP,
  SET_BETSLIP_MODE,
  SET_LAYOUT,
  SET_LIVE_CHAT,
  SET_MAIN_MENU,
  SET_MOBILE_DEVICE,
  SET_SIDEBAR_LEFT,
  SET_SIDEBAR_RIGHT,
  WALLET_UPDATE
} from '../actions'
import { BETSLIP_MODE, BONUS_TYPE, LIVECHAT_VISIBILITY } from '../constants'

/*
  Application State Manager
*/
export const ApplicationContext = React.createContext()
export const ApplicationDataProvider = ApplicationContext.Provider

const DEFAULT_STATE = {
  authenticated: false,
  /*
    Because StorageManager working async, we can assume
    that state resolved & loading flags initially will be not resolved
  */
  resolved: false,
  loading: true,

  account: {},
  wallet: {},
  betsCount: {},

  layout: {
    mobileDevice: false,
    sideBarLeft: true,
    sideBarRight: true,
    mainMenu: false,
    liveChat: LIVECHAT_VISIBILITY.HIDDEN
  },

  betSlip: {
    mode: BETSLIP_MODE.DEFAULT
  }
}

const MOBILE_DEFAULT_LAYOUT_STATE = {
  mobileDevice: true,
  tabletDevice: false,
  desktopDevice: false,
  sideBarLeft: false,
  sideBarRight: false,
  mainMenu: false
}

const getLayoutState = (layout, payload) => {
  if (layout.mobileDevice) {
    return {
      ...MOBILE_DEFAULT_LAYOUT_STATE,
      liveChat: layout.liveChat,
      ...payload
    }
  } else {
    return {
      ...layout,
      ...payload
    }
  }
}

/*
  Creating initial application state
*/
const createApplicationInitialState = () => {
  const connected = WSManager.connected
  const id = WSManager.connectionId

  return {
    ...DEFAULT_STATE,
    preferences: {},
    connection: {
      id,
      connected
    }
  }
}

/*
  Root application state handler
 */
export const rootReducer = (state, action) => {
  logger.debug('[state action]', '🏹', `[${action.type}]`, action.payload)

  switch (action.type) {
    case ACCOUNT_AUTHORIZED:
      return {
        ...state,
        authenticated: true,
        resolved: true,
        loading: false,
        account: action.payload.client,
        wallet: action.payload.wallet,
        layout: getLayoutState(state.layout, {
          mainMenu: false
        })
      }
    case ACCOUNT_LOGOUT:
      return {
        ...state,
        authenticated: false,
        resolved: true,
        loading: false,
        account: DEFAULT_STATE.account,
        wallet: DEFAULT_STATE.wallet,
        betsCount: DEFAULT_STATE.betsCount
      }
    case ACCOUNT_RESOLVING:
      return {
        ...state,
        resolved: false,
        loading: true
      }

    case ACCOUNT_VERIFICATION_UPDATE:
      return {
        ...state,
        account: {
          ...state.account,
          clientActivityState: (action.payload.data.activityState || state.account.clientActivityState),
          clientVerificationState: (action.payload.data.verificationState || state.account.clientVerificationState)
        }
      }

    case ACCOUNT_UPDATE:
      return {
        ...state,
        account: {
          ...state.account,
          ...action.payload
        }
      }

    case BETS_COUNT_UPDATE:
      return {
        ...state,
        betsCount: {
          // ...state.betsCount,
          ...action.payload
        }
      }

    case WALLET_UPDATE:
      return {
        ...state,
        wallet: {
          ...state.wallet,
          ...action.payload
        }
      }

    case BONUS_ACTIVATE:
      return {
        ...state,
        wallet: {
          ...state.wallet,
          bonuses: [
            ...(state.wallet.bonuses || []),
            action.payload
          ]
        }
      }

    case BONUS_DEACTIVATE:
      return {
        ...state,
        wallet: {
          ...state.wallet,
          bonuses: (state.wallet.bonuses || []).filter(b => b?.id !== action.payload?.id)
        }
      }

    case BONUS_STATUS: {
      let walletBonuses = state.wallet.bonuses || []
      const bonus = action.payload
      const idx = walletBonuses.findIndex(b => b?.id === bonus?.id)
      if (idx >= 0) {
        walletBonuses[idx] = {
          ...walletBonuses[idx],
          ...bonus
        }
      } else {
        walletBonuses = [
          ...walletBonuses,
          bonus
        ]
      }
      return {
        ...state,
        wallet: {
          ...state.wallet,
          bonuses: walletBonuses.filter(b => {
            // todo: Bonus Status is not always included in APIs???!!!!
            if (b.status && b.status !== 'ACTIVATED' && [
              BONUS_TYPE.FREEBET,
              BONUS_TYPE.BET_RETURN,
              BONUS_TYPE.ODDS_BOOST,
              BONUS_TYPE.PROMO_ODDS
            ].includes(b.bonusType)) {
              return false
            }
            return true
          })
        }
      }
    }

    case PREFERENCES_UPDATE:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          ...action.payload
        }
      }
    case CONNECTION_UPDATE:
      return {
        ...state,
        connection: {
          ...state.connection,
          ...action.payload
        }
      }

    case SET_MOBILE_DEVICE:
      return {
        ...state,
        layout: getLayoutState(state.layout, {
          mobileDevice: action.payload
        })
      }

    case SET_SIDEBAR_LEFT:
      if (state.layout.desktopDevice) {
        return {
          ...state,
          layout: getLayoutState(state.layout, {
            sideBarLeft: action.payload
          })
        }
      } else {
        return {
          ...state,
          layout: getLayoutState(state.layout, {
            sideBarLeft: action.payload,
            sideBarRight: state.layout.sideBarRight ? !action.payload : false
          })
        }
      }

    case SET_SIDEBAR_RIGHT:
      if (state.layout.desktopDevice) {
        return {
          ...state,
          layout: getLayoutState(state.layout, {
            sideBarRight: action.payload
          })
        }
      } else {
        return {
          ...state,
          layout: getLayoutState(state.layout, {
            sideBarRight: action.payload,
            sideBarLeft: state.layout.sideBarLeft ? !action.payload : false
          })
        }
      }

    case SET_MAIN_MENU:
      return {
        ...state,
        layout: getLayoutState(state.layout, {
          mainMenu: action.payload
        })
      }

    case SET_LIVE_CHAT:
      return {
        ...state,
        layout: getLayoutState(state.layout, {
          liveChat: action.payload
        })
      }

    case SET_LAYOUT:
      return {
        ...state,
        layout: getLayoutState(state.layout, {
          ...action.payload
        })
      }

    case SET_BETSLIP:
      return {
        ...state,
        betSlip: {
          ...state.betSlip,
          ...action.payload
        }
      }

    case SET_BETSLIP_MODE:
      return {
        ...state,
        betSlip: {
          mode: action.payload
        }
      }

    default:
      logger.error('[state]', `Action [${action.type}] dont have handler`)
      return state
  }
}

/*
  Hook for consuming global application context (state)
 */
export const useApplicationState = () => {
  return React.useContext(ApplicationContext)
}

/*
  Using initially for creating application global state
 */
export const createApplicationState = options => {
  return React.useReducer(
    rootReducer,
    options,
    createApplicationInitialState
  )
}
